import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import localStorageUtil from '../localStorage';
import { getCurrentLocation } from '../locationUtils';

function generateNewFileName(originalFileName, uid) {
  const dotIndex = originalFileName.lastIndexOf('.');
  const fileName = originalFileName.substring(0, dotIndex);
  const fileExt = originalFileName.substring(dotIndex + 1);
  return `${fileName}-${uid}-${Date.now()}.${fileExt}`;
}

export const useUploadToS3 = ({
  url,
  type: defaultType,
  id: defaultId,
  clientId: defaultClientId,
  onUpload,
  onError,
}) => {
  const userDetails = localStorageUtil.getItem('userDetails');

  return useMutation({
    mutationFn: async (variables) => {
      // Handle both legacy (array of files) and new (object with files, type, id, clientId) formats
      let files = Array.isArray(variables) ? variables : variables.files;
      const type = (variables && !Array.isArray(variables) ? variables.type : undefined) || defaultType;
      const id = (variables && !Array.isArray(variables) ? variables.id : undefined) || defaultId;
      const clientId = (variables && !Array.isArray(variables) ? variables.clientId : undefined) || defaultClientId;

      if (!files?.length) return [];

      const location = await getCurrentLocation();

      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();

        formData.append('file', file);
        formData.append('uid', userDetails?.uid);
        formData.append('uploadedBy', userDetails?.name);
        formData.append('uploadedByRole', userDetails?.roles?.[0]);
        formData.append('overwrite', true);
        formData.append(
          'fileName',
          generateNewFileName(file.name, userDetails?.uid)
        );

        const response = await axios.post(url, formData, {
          params: {
            type,
            id,
            clientId,
            ...location,
          },
        });

        let data = response?.data;

        // normalize → array of URLs
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          data = Object.values(data)
            .flat(Infinity)
            .filter((v) => typeof v === 'string' && v.startsWith('http'));
        } else if (typeof data === 'string') {
          data = [data];
        }

        return data || [];
      });

      return (await Promise.all(uploadPromises)).flat(Infinity);
    },

    onSuccess: (data) => {
      onUpload?.(data);
    },

    onError: (error) => {
      console.error('Upload error:', error);
      onError?.(error);
    },
  });
};
